import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle2, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token'); // Capturamos la llave secreta de la URL
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const { resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      return toast.error("El enlace no es válido. Solicitá uno nuevo.");
    }
    if (password.length < 6) {
      return toast.error("La contraseña debe tener al menos 6 caracteres.");
    }
    if (password !== confirmPassword) {
      return toast.error("Las contraseñas no coinciden.");
    }

    setLoading(true);
    try {
      await resetPassword(token, password);
      setSuccess(true);
      toast.success("¡Contraseña actualizada!");
      setTimeout(() => navigate('/auth'), 3000); // Lo mandamos al login
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al cambiar la contraseña. El enlace pudo haber caducado.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Fondo estético */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none" />

      <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl w-full max-w-md shadow-2xl relative z-10">
        
        <div className="w-14 h-14 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6 border border-blue-500/20 mx-auto">
          <KeyRound size={28} className="text-blue-400" />
        </div>

        <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Nueva Contraseña</h2>
            <p className="text-slate-400 text-sm">
                Asegurate de que tenga al menos 6 caracteres.
            </p>
        </div>

        {success ? (
            <div className="text-center space-y-4 animate-fade-in-up">
                <div className="flex justify-center text-emerald-500 mb-4">
                    <CheckCircle2 size={64} />
                </div>
                <h3 className="text-white font-bold text-xl">¡Todo listo!</h3>
                <p className="text-slate-400 text-sm">Tu contraseña ha sido actualizada. Te estamos redirigiendo...</p>
            </div>
        ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-4">
                    <div className="relative">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            placeholder="Nueva contraseña"
                            required
                            className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-all"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                        >
                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                    </div>

                    <div>
                        <input
                            type={showPassword ? 'text' : 'password'}
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            placeholder="Repetir nueva contraseña"
                            required
                            className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 transition-all"
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2 mt-4 disabled:opacity-50"
                >
                    {loading ? "Actualizando..." : "Guardar contraseña"}
                </button>
            </form>
        )}
      </div>
    </div>
  );
}
